import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const SEO = ({ title, description, image, url, article = false }) => {
    const { t, i18n } = useTranslation();

    const siteTitle = t('nav.brandName', { defaultValue: 'Saraya Auto Sales' });
    const defaultDescription = t('seo.description', { defaultValue: "Trouvez votre voiture d'occasion idéale parmi notre sélection de véhicules de qualité à Dakar, Sénégal" });
    const defaultKeywords = t('seo.keywords', { defaultValue: "voitures d'occasion Dakar, achat voiture Dakar, vente voiture Dakar, voitures d'occasion Sénégal, SUV d'occasion Dakar, 4x4 d'occasion Sénégal, berlines d'occasion Dakar, voitures à vendre Dakar, concessionnaire automobile Dakar, auto occasion Dakar" });

    const fullTitle = title ? `${title} | ${siteTitle}` : `${siteTitle} | ${t('seo.tagline', { defaultValue: "Voitures d'occasion à Dakar" })}`;
    const metaDescription = description || defaultDescription;
    const metaKeywords = defaultKeywords;
    const metaImage = image || `https://www.sarayaauto.com/saraya_logo_new.png`;

    // Canonical URL - Lock to production domain with trailing slash for root
    const productionDomain = 'https://www.sarayaauto.com/';
    const path = url || globalThis.location.pathname;
    let cleanPath = '';
    if (path !== '/') {
        cleanPath = path.startsWith('/') ? path.substring(1) : path;
    }
    const canonicalUrl = `${productionDomain}${cleanPath}`.split('?')[0];

    const currentLanguage = i18n.language || 'fr';

    // Alternate languages for hreflang
    const baseUrl = productionDomain;

    return (
        <Helmet>
            {/* Standard Metadata */}
            <title>{fullTitle}</title>
            <meta name="description" content={metaDescription} />
            {metaKeywords && <meta name="keywords" content={metaKeywords} />}

            {/* Canonical URL - SEO Critical */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Language Configuration */}
            <html lang={currentLanguage} />
            <meta httpEquiv="content-language" content={currentLanguage} />

            {/* Hreflang for bilingual support */}
            <link rel="alternate" href={baseUrl} hrefLang="x-default" />
            <link rel="alternate" href={baseUrl} hrefLang="fr" />
            <link rel="alternate" href={baseUrl} hrefLang="en" />

            {/* Robots - Allow indexing */}
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={article ? "article" : "website"} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={metaDescription} />
            <meta property="og:image" content={metaImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:site_name" content={siteTitle} />
            <meta property="og:locale" content={currentLanguage === 'fr' ? 'fr_SN' : 'en_US'} />

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={metaDescription} />
            <meta name="twitter:image" content={metaImage} />
            <meta name="twitter:creator" content="@sarayaautosales" />
            <meta name="twitter:site" content="@sarayaautosales" />

            {/* Additional SEO tags */}
            <meta name="author" content="Saraya Auto Sales" />
            <meta name="geo.region" content="SN-DK" />
            <meta name="geo.placename" content="Dakar" />
            <meta name="geo.position" content="14.7645042;-17.4957462" />
            <meta name="ICBM" content="14.7645042, -17.4957462" />
        </Helmet>
    );
};

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    url: PropTypes.string,
    article: PropTypes.bool,
};

export default SEO;
